/**
 * The browser's copy of the server's video URL rule (backend/modules/video_urls.py): a
 * pasted YouTube or Vimeo share URL becomes its framable embed form.
 *
 *   youtube.com/watch?v=ID, youtu.be/ID, youtube.com/shorts/ID, youtube.com/live/ID
 *       -> https://www.youtube.com/embed/ID   (+ ?start=N when the share URL carried t=)
 *   vimeo.com/ID, vimeo.com/ID/HASH, vimeo.com/ID?h=HASH
 *       -> https://player.vimeo.com/video/ID  (+ ?h=HASH)
 *
 * WHY A MIRROR. A pathway video material (contract §11.1) is accepted only when the server's
 * `normalize_video_url` turns it into one of those two embed forms (`is_embed_url`);
 * anything else is a 422 VIDEO_URL_UNSUPPORTED. The builder previews the video and flags an
 * unsupported URL while the practitioner types, so it must reach the same verdict as the
 * server, or a URL the preview played would be refused on Save. The same inputs are pinned
 * on both sides (lib/__tests__/videoUrls.test.js mirrors the backend's cases).
 *
 * The URL is taken apart by hand, the way Python's `urlparse` does, rather than with
 * `new URL()`: the WHATWG parser throws on inputs `urlparse` accepts (an out-of-range port,
 * for one) and rewrites paths, and either difference would split the two verdicts.
 *
 * Pure: no React, no transport.
 */

const YOUTUBE_HOSTS = new Set([
  "youtube.com", "www.youtube.com", "m.youtube.com", "music.youtube.com",
  "youtube-nocookie.com", "www.youtube-nocookie.com",
]);
const YOUTU_BE_HOSTS = new Set(["youtu.be", "www.youtu.be"]);
const VIMEO_HOSTS = new Set(["vimeo.com", "www.vimeo.com"]);

// Only the alphabet of a YouTube id is contractual, not its length (see the server).
const VIDEO_ID = /^[A-Za-z0-9_-]{6,64}$/;
const VIMEO_HASH = /^[A-Za-z0-9]{6,32}$/;
const SCHEME = /^[A-Za-z][A-Za-z0-9+.-]*:\/\//;
const TIMECODE = /^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?$/;
const EMBED = /^https:\/\/(?:www\.youtube\.com\/embed\/[A-Za-z0-9_-]{6,64}(?:\?start=\d+)?|player\.vimeo\.com\/video\/\d+(?:\?h=[A-Za-z0-9]{6,32})?)$/;

/** `urlparse`'s percent-decoding: an undecodable sequence is kept as written. */
const decode = (s) => {
  try {
    return decodeURIComponent(s.replace(/\+/g, " "));
  } catch {
    return s;
  }
};

/**
 * `parse_qs`: name -> values, blank values dropped (`keep_blank_values=False`).
 *
 * @param {string} query  the part after "?", without the fragment
 * @returns {Object<string, string[]>}
 */
function parseQuery(query) {
  const out = {};
  for (const pair of query.split("&")) {
    if (!pair) continue;
    const eq = pair.indexOf("=");
    if (eq < 0) continue;                       // "name" alone carries no value
    const name = decode(pair.slice(0, eq));
    const value = decode(pair.slice(eq + 1));
    if (!value) continue;
    (out[name] || (out[name] = [])).push(value);
  }
  return out;
}

/**
 * The parts of an absolute URL that the rule reads, split the way `urlparse` splits them:
 * `scheme://netloc/path;params?query#fragment`.
 *
 * @param {string} url  starts with a scheme and "://"
 * @returns {{ host: string, segments: string[], query: Object<string, string[]> }}
 */
function splitUrl(url) {
  let rest = url.slice(url.indexOf("://") + 3);
  const hash = rest.indexOf("#");
  if (hash >= 0) rest = rest.slice(0, hash);
  let query = "";
  const q = rest.indexOf("?");
  if (q >= 0) {
    query = rest.slice(q + 1);
    rest = rest.slice(0, q);
  }
  const slash = rest.indexOf("/");
  const netloc = slash >= 0 ? rest.slice(0, slash) : rest;
  let path = slash >= 0 ? rest.slice(slash) : "";
  // `urlparse` moves ";params" of the LAST path segment out of the path.
  const lastSlash = path.lastIndexOf("/");
  const semi = path.indexOf(";", lastSlash + 1);
  if (semi >= 0) path = path.slice(0, semi);

  // hostname: no user info, no port, lower case ("[v6]" keeps its address).
  const hostPort = netloc.slice(netloc.lastIndexOf("@") + 1);
  let host;
  if (hostPort.startsWith("[")) {
    const close = hostPort.indexOf("]");
    host = close >= 0 ? hostPort.slice(1, close) : hostPort.slice(1);
  } else {
    host = hostPort.split(":")[0];
  }
  return {
    host: host.toLowerCase(),
    segments: path.split("/").filter(Boolean),
    query: parseQuery(query),
  };
}

/** A `t=` share value -> seconds for the embed `start=`, or null (bare seconds or "1h2m3s" only). */
function youtubeStartSeconds(raw) {
  const val = String(raw || "").trim().toLowerCase();
  if (!val) return null;
  if (/^\d+$/.test(val)) return Number(val);
  const m = TIMECODE.exec(val);
  if (!m || !(m[1] || m[2] || m[3])) return null;
  return Number(m[1] || 0) * 3600 + Number(m[2] || 0) * 60 + Number(m[3] || 0);
}

/** The embed URL for `id`, or "" when it does not look like a video id. */
function youtubeEmbed(id, query) {
  if (!VIDEO_ID.test(id || "")) return "";
  const start = youtubeStartSeconds((query.t || [""])[0]);
  return `https://www.youtube.com/embed/${id}${start ? `?start=${start}` : ""}`;
}

/** The Vimeo player URL; an unlisted video's privacy hash is load-bearing (403 without it). */
function vimeoPlayer(id, privacyHash) {
  const player = `https://player.vimeo.com/video/${id}`;
  return VIMEO_HASH.test(privacyHash || "") ? `${player}?h=${privacyHash}` : player;
}

/**
 * A pasted YouTube or Vimeo share URL in its embed form. Anything else, an embed URL
 * included, comes back unchanged (trimmed). Idempotent.
 *
 * @param {string} url
 * @returns {string}
 */
export function normalizeVideoUrl(url) {
  const raw = String(url ?? "").trim();
  if (!raw) return raw;
  // A schemeless paste ("youtu.be/xyz") is read with an https:// prefix, as on the server.
  const { host, segments, query } = splitUrl(SCHEME.test(raw) ? raw : `https://${raw.replace(/^\/+/, "")}`);

  if (YOUTU_BE_HOSTS.has(host)) return youtubeEmbed(segments[0] || "", query) || raw;
  if (YOUTUBE_HOSTS.has(host)) {
    if (segments[0] === "watch") return youtubeEmbed((query.v || [""])[0], query) || raw;
    if (segments.length >= 2 && (segments[0] === "shorts" || segments[0] === "live")) {
      return youtubeEmbed(segments[1], query) || raw;
    }
    return raw;
  }
  if (VIMEO_HOSTS.has(host) && segments.length > 0 && /^\d+$/.test(segments[0])) {
    const privacyHash = segments.length >= 2 ? segments[1] : (query.h || [""])[0];
    return vimeoPlayer(segments[0], privacyHash);
  }
  return raw;
}

/**
 * Whether `url` is exactly one of the two framable embed forms (`is_embed_url`).
 *
 * @param {string} url
 * @returns {boolean}
 */
export const isEmbedUrl = (url) => EMBED.test(String(url ?? ""));

/**
 * The embed URL a video material plays, or "" when `url` is not a YouTube or Vimeo video
 * (the server's VIDEO_URL_UNSUPPORTED). "" for an empty `url` too.
 *
 * @param {string} url
 * @returns {string}
 */
export function videoEmbedUrl(url) {
  const normalized = normalizeVideoUrl(url);
  return isEmbedUrl(normalized) ? normalized : "";
}

/**
 * Which player an embed URL plays in: "youtube", "vimeo", or "" for anything that is not
 * one of the two embed forms.
 *
 * @param {string} url
 * @returns {"youtube"|"vimeo"|""}
 */
export function embedProvider(url) {
  const s = String(url ?? "");
  if (!isEmbedUrl(s)) return "";
  return s.startsWith("https://www.youtube.com/") ? "youtube" : "vimeo";
}

/**
 * The embed URL with its player's JavaScript API switched on, applied AT RENDER TIME only:
 * the stored URL (and the server's `is_embed_url` rule it satisfies) is unchanged.
 *
 *   YouTube  + enablejsapi=1&origin=<page origin>  (the IFrame Player API refuses to talk
 *            to a page whose origin it was not told)
 *   Vimeo    + api=1                               (the postMessage API)
 *
 * An existing query (`?start=`, `?h=`) is kept; the parameters are appended after it.
 * Anything that is not an embed URL comes back unchanged.
 *
 * @param {string} url     an embed URL (videoEmbedUrl's output)
 * @param {string} origin  `window.location.origin`
 * @returns {string}
 */
export function withPlayerApi(url, origin) {
  const provider = embedProvider(url);
  if (!provider) return String(url ?? "");
  const sep = url.includes("?") ? "&" : "?";
  if (provider === "youtube") {
    return `${url}${sep}enablejsapi=1&origin=${encodeURIComponent(String(origin || ""))}`;
  }
  return `${url}${sep}api=1`;
}
